import { createReducer, on } from '@ngrx/store';
import { QueryConfig } from '@wonder-bi-front/query-engine/interfaces/query.interface';
import { ChatMessage, ConversationSummary, LlmModelOption, ThinkingLevel } from './ai-chat.types';
import { AIActions } from './ai.actions';

export const aiFeatureKey = 'ai';

export interface State {
  // Conversational state
  conversationId: string | null;
  messages: ChatMessage[];
  /** The user's past conversations for the history sidebar. */
  conversations: ConversationSummary[];
  generatedQueryConfig: QueryConfig | null;
  generatedChartConfig: Record<string, unknown> | null;
  suggestions: string[];
  /** The user's verdict per turn, keyed by turn id. Optimistic: set on click, rolled back on failure. */
  /** LLMs the user may switch to; empty unless they hold AI_MODEL_SELECT. */
  availableModels: LlmModelOption[];
  /** The model selector is on (AI_MODEL_SELECTOR); off, each agent runs on its own model and nothing is picked. */
  modelSelectorEnabled: boolean;
  /** LLM the next turn runs against; null means the environment default. */
  selectedModel: string | null;
  /** Model the server falls back to, used to restore the picker for conversations with none pinned. */
  defaultModel: string | null;
  /** Thinking levels a turn can be run at, as the server reports them. */
  thinkingLevels: ThinkingLevel[];
  /** Server-side switch: which query path the turns are running on. */
  sqlFirst: boolean;
  /** Level picked for the conversation; null leaves the provider's own default. */
  selectedThinkingLevel: ThinkingLevel | null;
  /** Score the user gave the current session, 1-5; null while unrated. */
  sessionRating: number | null;
  pendingMessage: string | null;
  pendingQueryConfig: Record<string, unknown> | null;
  pendingChartConfig: Record<string, unknown> | null;
  pendingContext: 'query' | 'chart' | null;
  /** Prompt of a turn the user stopped, handed back to the composer; not a failure. */
  cancelledMessage: string | null;
  cancelledQueryConfig: Record<string, unknown> | null;
  cancelledChartConfig: Record<string, unknown> | null;
  cancelledContext: 'query' | 'chart' | null;
  failedMessage: string | null;
  failedQueryConfig: Record<string, unknown> | null;
  failedChartConfig: Record<string, unknown> | null;
  failedContext: 'query' | 'chart' | null;
  /** Category of the last failure (e.g. 'llm_connection'), drives the localised error label. */
  failedErrorCode: string | null;
  /** Exact cause of the last failure; only present when the server exposes internal errors (dev/pre). */
  failedErrorDetail: string | null;
  loading: boolean;
  httpResult: { action: unknown; error?: unknown };
}

export const initialState: State = {
  conversationId: null,
  messages: [],
  conversations: [],
  generatedQueryConfig: null,
  generatedChartConfig: null,
  suggestions: [],
  availableModels: [],
  selectedModel: null,
  defaultModel: null,
  modelSelectorEnabled: false,
  thinkingLevels: [],
  sqlFirst: false,
  selectedThinkingLevel: null,
  sessionRating: null,
  pendingMessage: null,
  pendingQueryConfig: null,
  pendingChartConfig: null,
  pendingContext: null,
  cancelledMessage: null,
  cancelledQueryConfig: null,
  cancelledChartConfig: null,
  cancelledContext: null,
  failedMessage: null,
  failedQueryConfig: null,
  failedChartConfig: null,
  failedContext: null,
  failedErrorCode: null,
  failedErrorDetail: null,
  loading: false,
  httpResult: { action: '' },
};

export const reducer = createReducer(
  initialState,

  // ── Chat ─────────────────────────────────────────────────────────────────
  on(
    AIActions.sendMessage,
    (state, { message, currentQueryConfig, currentChartConfig, context }): State => ({
      ...state,
      loading: true,
      pendingMessage: message,
      pendingQueryConfig: currentQueryConfig ?? null,
      pendingChartConfig: currentChartConfig ?? null,
      pendingContext: context ?? null,
      cancelledMessage: null,
      cancelledQueryConfig: null,
      cancelledChartConfig: null,
      cancelledContext: null,
      failedMessage: null,
      failedQueryConfig: null,
      failedChartConfig: null,
      failedContext: null,
      failedErrorCode: null,
      failedErrorDetail: null,
    })
  ),

  on(AIActions.sendMessageSuccess, (state, { conversationId, newMessages }): State => {
    const lastQueryConfig =
      [...newMessages].reverse().find((m) => m.queryConfig != null)?.queryConfig ??
      state.generatedQueryConfig;
    const lastChartConfig =
      [...newMessages].reverse().find((m) => m.chartConfig != null)?.chartConfig ??
      state.generatedChartConfig;

    return {
      ...state,
      loading: false,
      pendingMessage: null,
      pendingQueryConfig: null,
      pendingChartConfig: null,
      pendingContext: null,
      failedMessage: null,
      failedQueryConfig: null,
      failedChartConfig: null,
      failedContext: null,
      failedErrorCode: null,
      failedErrorDetail: null,
      conversationId: conversationId || state.conversationId,
      messages: [...state.messages, ...newMessages],
      generatedQueryConfig: lastQueryConfig ?? null,
      generatedChartConfig: lastChartConfig ?? null,
    };
  }),

  on(
    AIActions.cancelMessage,
    (state): State => ({
      ...state,
      loading: false,
      cancelledMessage: state.pendingMessage,
      cancelledQueryConfig: state.pendingQueryConfig,
      cancelledChartConfig: state.pendingChartConfig,
      cancelledContext: state.pendingContext,
      pendingMessage: null,
      pendingQueryConfig: null,
      pendingChartConfig: null,
      pendingContext: null,
    })
  ),

  on(
    AIActions.sendMessageFailure,
    (state): State => ({
      ...state,
      loading: false,
      failedMessage: state.pendingMessage,
      failedQueryConfig: state.pendingQueryConfig,
      failedChartConfig: state.pendingChartConfig,
      failedContext: state.pendingContext,
      // Failure of the SSE request itself (network to our backend / non-2xx before the stream opens);
      // the transport gives us no category, so fall back to the generic "internal" label.
      failedErrorCode: 'internal',
      failedErrorDetail: null,
      pendingMessage: null,
      pendingQueryConfig: null,
      pendingChartConfig: null,
      pendingContext: null,
    })
  ),

  // ── Streaming ─────────────────────────────────────────────────────────────
  on(
    AIActions.streamStart,
    (state, { userMessage }): State => ({
      ...state,
      messages: [...state.messages, userMessage],
    })
  ),

  on(
    AIActions.streamToolMessage,
    (state, { message }): State => ({
      ...state,
      messages: [...state.messages, message],
    })
  ),

  on(AIActions.streamComplete, (state, { conversationId, agentMessages }): State => {
    const lastQueryConfig =
      [...agentMessages].reverse().find((m) => m.queryConfig != null)?.queryConfig ??
      state.generatedQueryConfig;
    const lastChartConfig =
      [...agentMessages].reverse().find((m) => m.chartConfig != null)?.chartConfig ??
      state.generatedChartConfig;

    // Tool/assistant messages were already appended live via streamToolMessage; the `done` event
    // resends the whole turn in agentMessages. Merge by id (final version wins, order preserved)
    // instead of blindly concatenating, which duplicated ids and triggered NG0955 in the @for.
    const byId = new Map(state.messages.map((m) => [m.id, m]));
    for (const m of agentMessages) byId.set(m.id, m);

    return {
      ...state,
      loading: false,
      pendingMessage: null,
      pendingQueryConfig: null,
      pendingChartConfig: null,
      pendingContext: null,
      failedMessage: null,
      failedQueryConfig: null,
      failedChartConfig: null,
      failedContext: null,
      failedErrorCode: null,
      failedErrorDetail: null,
      conversationId: conversationId || state.conversationId,
      messages: Array.from(byId.values()),
      generatedQueryConfig: lastQueryConfig ?? null,
      generatedChartConfig: lastChartConfig ?? null,
    };
  }),

  on(
    AIActions.streamError,
    (state, { code, detail }): State => ({
      ...state,
      loading: false,
      failedMessage: state.pendingMessage,
      failedQueryConfig: state.pendingQueryConfig,
      failedChartConfig: state.pendingChartConfig,
      failedContext: state.pendingContext,
      failedErrorCode: code ?? 'internal',
      failedErrorDetail: detail ?? null,
      pendingMessage: null,
      pendingQueryConfig: null,
      pendingChartConfig: null,
      pendingContext: null,
    })
  ),

  on(AIActions.appendLocalMessages, (state, { messages }): State => ({ ...state, messages: [...state.messages, ...messages] })),
  on(AIActions.removeMessage, (state, { id }): State => ({ ...state, messages: state.messages.filter((m) => m.id !== id) })),

  on(
    AIActions.clearConversation,
    (state): State => ({
      ...state,
      conversationId: null,
      sessionRating: null,
      messages: [],
      generatedQueryConfig: null,
      generatedChartConfig: null,
      suggestions: [],
      pendingMessage: null,
      pendingQueryConfig: null,
      pendingChartConfig: null,
      pendingContext: null,
      failedMessage: null,
      failedQueryConfig: null,
      failedChartConfig: null,
      failedContext: null,
      failedErrorCode: null,
      failedErrorDetail: null,
    })
  ),

  // ── Conversation history ───────────────────────────────────────────────────
  on(
    AIActions.loadConversationsSuccess,
    (state, { conversations }): State => ({
      ...state,
      conversations,
    })
  ),

  on(
    AIActions.startNewConversation,
    (state): State => ({
      ...state,
      conversationId: null,
      sessionRating: null,
      messages: [],
      generatedQueryConfig: null,
      generatedChartConfig: null,
      suggestions: [],
      pendingMessage: null,
      pendingQueryConfig: null,
      pendingChartConfig: null,
      pendingContext: null,
      failedMessage: null,
      failedQueryConfig: null,
      failedChartConfig: null,
      failedContext: null,
      failedErrorCode: null,
      failedErrorDetail: null,
      loading: false,
    })
  ),

  on(AIActions.loadConversation, (state): State => ({ ...state, loading: true })),

  on(
    AIActions.loadConversationSuccess,
    (state, { conversationId, messages, model, thinkingLevel, rating }): State => {
      const lastQueryConfig =
        [...messages].reverse().find((m) => m.queryConfig != null)?.queryConfig ?? null;
      const lastChartConfig =
        [...messages].reverse().find((m) => m.chartConfig != null)?.chartConfig ?? null;
      return {
        ...state,
        loading: false,
        conversationId,
        messages,
        selectedModel: model ?? state.defaultModel,
        selectedThinkingLevel: thinkingLevel ?? null,
        sessionRating: rating ?? null,
        generatedQueryConfig: lastQueryConfig,
        generatedChartConfig: lastChartConfig,
        suggestions: [],
        pendingMessage: null,
        pendingQueryConfig: null,
        pendingChartConfig: null,
        pendingContext: null,
        failedMessage: null,
        failedQueryConfig: null,
        failedChartConfig: null,
        failedContext: null,
        failedErrorCode: null,
        failedErrorDetail: null,
      };
    }
  ),

  on(AIActions.loadConversationFailure, (state): State => ({ ...state, loading: false })),

  // ── Deterministic chart-config generation ──────────────────────────────────
  on(AIActions.generateChartConfig, (state): State => ({ ...state, loading: true })),
  on(
    AIActions.generateChartConfigSuccess,
    (state, { chartConfig }): State => ({
      ...state,
      loading: false,
      generatedChartConfig: chartConfig,
    })
  ),
  on(AIActions.generateChartConfigFailure, (state): State => ({ ...state, loading: false })),

  // ── Suggestions ───────────────────────────────────────────────────────────
  // ── Session rating ───────────────────────────────────────────────────────
  on(AIActions.rateSession, (state, { rating }): State => ({ ...state, sessionRating: rating })),

  // Roll the optimistic score back, so the stars never claim one the server did not take.
  on(AIActions.rateSessionFailure, (state): State => ({ ...state, sessionRating: null })),

  // ── Model selection ──────────────────────────────────────────────────────
  on(
    AIActions.loadModelsSuccess,
    (state, { models, defaultModel, thinkingLevels, sqlFirst, selectorEnabled }): State => ({
      ...state,
      availableModels: models,
      defaultModel,
      thinkingLevels,
      sqlFirst,
      modelSelectorEnabled: selectorEnabled === true,
      selectedModel: state.selectedModel ?? defaultModel,
    })
  ),

  on(
    AIActions.selectModel,
    (state, { model }): State => ({
      ...state,
      selectedModel: model,
    })
  ),

  on(
    AIActions.selectThinkingLevel,
    (state, { thinkingLevel }): State => ({
      ...state,
      selectedThinkingLevel: thinkingLevel,
    })
  ),

  on(AIActions.loadSuggestionsSuccess, (state, { suggestions }): State => {
    // Attach the suggestions to the first assistant message (the welcome message)
    const messages =
      state.messages.length > 0 &&
      state.messages[0].role === 'assistant' &&
      !state.messages[0].suggestions?.length
        ? [{ ...state.messages[0], suggestions }, ...state.messages.slice(1)]
        : state.messages;
    return { ...state, suggestions, messages };
  })
);
